import { apiBaseUrl, type EnvironmentId } from '@/config/environments';

import { forceRefreshToken, handleSessionExpired } from '@/lib/authBridge';
import type { ApiErrorBody } from './types';

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly body?: ApiErrorBody;

  constructor(status: number, message: string, code?: string, body?: ApiErrorBody) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export type RequestContext = {
  env: EnvironmentId;
  /** Partner key — sent as the `api-key` header (tech-design §5 Auth). */
  apiKey: string;
};

type HttpOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

/**
 * Low-level call against the selected environment's `/v2` base URL. Throws {@link ApiError}
 * on non-2xx. Auth header is supplied by the caller (partner `api-key` or admin `x-admin-api-key`).
 */
export async function http<T>(
  env: EnvironmentId,
  path: string,
  { method = 'GET', body, headers = {}, signal }: HttpOptions = {}
): Promise<T> {
  const response = await fetch(`${apiBaseUrl(env)}${path}`, {
    method,
    signal,
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await response.text();
  const parsed = text ? safeJsonParse(text) : undefined;

  if (!response.ok) {
    const errBody = parsed as ApiErrorBody | undefined;
    const message = errBody?.error?.message ?? `Request to ${path} failed with ${response.status} ${response.statusText}`;
    throw new ApiError(response.status, message, errBody?.error?.code, errBody);
  }

  return parsed as T;
}

/** Partner-key authenticated request (`api-key` header). Extra headers (e.g. `Melio-Entity-Id`) merge in. */
export async function request<T>({ env, apiKey }: RequestContext, path: string, opts: HttpOptions = {}): Promise<T> {
  if (!apiKey) {
    throw new ApiError(401, 'Missing API key. Enter your partner key to call the API.');
  }
  return http<T>(env, path, { ...opts, headers: { 'api-key': apiKey, ...opts.headers } });
}

/**
 * Context for calls the signed-in dashboard makes against the payouts `/v2` API with the user's
 * short-lived access token (Authorization: Bearer) instead of a partner api-key — payouts resolves
 * the partner from the token, so the browser never holds the key. `getToken` refreshes/dedupes via
 * AppProvider.
 */
export type AuthContext = {
  env: EnvironmentId;
  getToken: () => Promise<string | null>;
};

/**
 * Access-token authenticated request (`Authorization: Bearer`). Extra headers merge in.
 *
 * On a 401 the access token is stale despite our clock (revoked, skew, or expired between check and
 * send): force a single coordinated refresh and retry once. If the refresh fails (the refresh token
 * has expired), hand off to the session-expired flow, which redirects to sign-in preserving where the
 * user was, and surface the 401 to the caller.
 */
export async function authRequest<T>({ env, getToken }: AuthContext, path: string, opts: HttpOptions = {}): Promise<T> {
  const token = await getToken();
  if (!token) {
    handleSessionExpired();
    throw new ApiError(401, 'Your session has expired. Please sign in again.');
  }
  const send = (bearer: string) => http<T>(env, path, { ...opts, headers: { Authorization: `Bearer ${bearer}`, ...opts.headers } });
  try {
    return await send(token);
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err;
    const refreshed = await forceRefreshToken();
    if (!refreshed) {
      handleSessionExpired();
      throw err;
    }
    return send(refreshed);
  }
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}
